import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { useSidebarData } from '../../../hooks/useSidebarData';
import { FlowService } from '../../../service/FlowService';
import { FolderService } from '../../../service/FolderService';
import { FormsService } from '../../../service/FormService';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreTabs } from '../../../store/storeTabs';
import CreateFlowModal from '../../Flow/CreateFlowModal';
import {
  type FlowModalTarget,
  flowTypeForModal,
  resolveFlowFolderLocation,
} from './resolveFlowFolderLocation';
import { isVirtualFolderIdBasic, parseSpaceIdFromFolderId } from './utils';

export interface CreateOrEditFlowModalState {
  isSharing: boolean;
  isEditing: boolean;
  type?: string;
  flow: {
    id: string;
    name: string;
  };
  folderId: string;
  spaceId?: string;
}

const initialModalData: CreateOrEditFlowModalState = {
  isSharing: false,
  isEditing: false,
  type: '',
  flow: { id: '', name: '' },
  folderId: '',
  spaceId: undefined,
};

export interface FlowModalActions {
  openRenameFlow: (flow: FlowModalTarget, folderIdHint: string) => void;
  openShareFlow: (flow: FlowModalTarget, folderIdHint: string) => void;
  duplicateFlow: (flow: FlowModalTarget, folderIdHint: string) => Promise<void>;
}

type FlowModalContextValue = FlowModalActions;

const FlowModalContext = createContext<FlowModalContextValue | null>(null);

export const useFlowModal = (): FlowModalContextValue => {
  const ctx = useContext(FlowModalContext);
  if (!ctx) {
    throw new Error('useFlowModal must be used within FlowModalProvider');
  }
  return ctx;
};

const resolveFolderAndSpace = (
  flow: FlowModalTarget,
  folderIdHint: string,
  privateFolders: ReturnType<typeof useSidebarData>['folders'],
  spaces: ReturnType<typeof useSidebarData>['spaces'],
): { folderId: string; spaceId: string } => {
  const resolved = resolveFlowFolderLocation(flow, privateFolders, spaces);
  let folderId = resolved.folderId;
  let spaceId = resolved.spaceId || '';

  if (
    !folderId &&
    folderIdHint &&
    !folderIdHint.startsWith('search-') &&
    !isVirtualFolderIdBasic(folderIdHint)
  ) {
    folderId = folderIdHint;
  }
  if (!spaceId && isVirtualFolderIdBasic(folderIdHint)) {
    const parsed = parseSpaceIdFromFolderId(folderIdHint);
    if (parsed) spaceId = parsed;
  }
  if (!spaceId && flow.spaceId) spaceId = flow.spaceId;
  return { folderId, spaceId };
};

export const FlowModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [openAddFlowModal, setOpenAddFlowModal] = useState(false);
  const [createOrEditFlow, setCreateOrEditFlow] =
    useState<CreateOrEditFlowModalState>(initialModalData);
  const [, setIsFlowModalOpen] = useState({
    isOpen: false,
    flowId: '',
  });
  const modalSpaceIdRef = useRef('');

  const {
    folders,
    setFolders,
    user: authUser,
    setAuthUser,
  } = useAuthStore(s => s);
  const { addFlow, updateFlow } = useFlowsStore(s => s);
  const updateTab = useStoreTabs(s => s.updateTab);
  const setNotification = useNotificationStore(s => s.setNotification);
  const {
    folders: sidebarFolders,
    spaces,
    refresh: refreshSidebar,
  } = useSidebarData();

  const resetModalData = useCallback(() => {
    modalSpaceIdRef.current = '';
    setCreateOrEditFlow(initialModalData);
  }, []);

  const onCreateFlowOrUpdate = useCallback(
    async (
      name: string,
      isEditing = false,
      id = '',
      folderId = '',
      onCopy = false,
      type = '',
      spaceIdArg = '',
    ) => {
      try {
        if (isEditing) {
          if (type === 'flow' || type === 'chat') {
            if (onCopy) {
              const createResponse = await FlowService.duplicateFlow({
                flowId: id,
                folderId: folderId || undefined,
                spaceId: spaceIdArg || undefined,
              });
              addFlow({
                id: createResponse.data?.id,
                name: createResponse.data?.name,
                index: createResponse.data?.index,
              });
            } else {
              const res = await FlowService.updateFlow(id, { name });
              updateTab({
                flowId: res.data?.id,
                flowName: res.data?.name,
              });
              updateFlow(res.data);
              const updatedFolders = folders.map(folder => ({
                ...folder,
                flows: folder.flows.map(flow =>
                  flow.id === res.data?.id
                    ? { ...flow, name: res.data?.name }
                    : flow,
                ),
              }));
              setFolders(updatedFolders);
            }
          }

          if (type === 'form') {
            if (onCopy) {
              const formToClone = await FormsService.getForm(id);
              const newForm = await FormsService.createForm({
                name: `${name} (Copy)`,
                flowId: formToClone.data.flows[0].id,
                folderId,
                index: 99,
              });
              await FormsService.update(newForm.data.id, {
                activeFlowId: formToClone.data.activeFlowId,
                activeFlows: formToClone.data.activeFlows,
                flows: formToClone.data.flows,
                formStyle: formToClone.data.formStyle,
                nodes: formToClone.data.nodes,
              });
            } else {
              const res = await FlowService.updateFlow(id, { name });
              updateTab({
                flowId: res.data?.id,
                flowName: res.data?.name,
              });
              updateFlow(res.data);
              const updatedFolders = folders.map(folder => ({
                ...folder,
                flows: folder.flows.map(flow =>
                  flow.id === res.data?.id
                    ? { ...flow, name: res.data?.name }
                    : flow,
                ),
              }));
              setFolders(updatedFolders);
            }
          }
        } else {
          const createResponse = await FlowService.createFlow({
            name,
            folderId,
            type,
          });
          addFlow({
            id: createResponse.data?.id,
            name: createResponse.data?.name,
            index: createResponse.data?.index,
          });
          if (type === 'chat' && authUser) {
            setAuthUser({
              ...authUser,
              chatsId: [...(authUser.chatsId || []), createResponse.data?.id!],
            });
          }
          navigate(
            `/dashboard-beta/agent/${createResponse.data.id}?folderId=${folderId}&type=flow`,
          );
        }

        if (folderId) {
          try {
            const res = await FolderService.getOne(folderId);
            const modifyFolders = folders.map(folder =>
              folder.id === folderId ? res.data : folder,
            );
            setFolders(modifyFolders);
          } catch {
            refreshSidebar();
          }
        } else {
          refreshSidebar();
        }
        setOpenAddFlowModal(false);
        resetModalData();
      } catch (error) {
        const anyError = error as Error & { message?: string };
        if (
          typeof anyError.message === 'string' &&
          anyError.message.includes('flows')
        ) {
          setNotification({
            type: NotificationType.Error,
            message: 'Agent limit reached. Upgrade your plan to add more.',
          });
        } else {
          setNotification({
            type: NotificationType.Error,
            message: getAPIErrorMessage(anyError),
          });
        }
        throw error;
      }
    },
    [
      folders,
      setFolders,
      addFlow,
      updateFlow,
      updateTab,
      navigate,
      refreshSidebar,
      resetModalData,
      setNotification,
      authUser,
      setAuthUser,
    ],
  );

  const onShareFlow = useCallback(
    async (id: string, email: string, name: string, companyId?: string) => {
      try {
        const body: { email: string; name: string; companyId?: string } = {
          email,
          name,
        };
        if (companyId) body.companyId = companyId;
        await FlowService.shareFlow(id, body);
        setNotification({
          type: NotificationType.Success,
          message: 'Agent shared successfully',
        });
        setOpenAddFlowModal(false);
        resetModalData();
      } catch (err) {
        const error = err as { message?: string };
        setNotification({
          type: NotificationType.Error,
          message: error.message || 'Something went wrong , sharing failed',
        });
      }
    },
    [setNotification, resetModalData],
  );

  const openRenameFlow = useCallback(
    (flow: FlowModalTarget, folderIdHint: string) => {
      const { folderId, spaceId } = resolveFolderAndSpace(
        flow,
        folderIdHint,
        sidebarFolders,
        spaces,
      );
      modalSpaceIdRef.current = spaceId;
      setCreateOrEditFlow({
        isSharing: false,
        isEditing: true,
        type: flowTypeForModal(flow),
        flow: { id: flow.id, name: flow.name },
        folderId,
        spaceId: spaceId || undefined,
      });
      setOpenAddFlowModal(true);
    },
    [sidebarFolders, spaces],
  );

  const openShareFlow = useCallback(
    (flow: FlowModalTarget, folderIdHint: string) => {
      const { folderId, spaceId } = resolveFolderAndSpace(
        flow,
        folderIdHint,
        sidebarFolders,
        spaces,
      );
      modalSpaceIdRef.current = spaceId;
      setCreateOrEditFlow({
        isSharing: true,
        isEditing: true,
        type: flowTypeForModal(flow),
        flow: { id: flow.id, name: flow.name },
        folderId,
        spaceId: spaceId || undefined,
      });
      setOpenAddFlowModal(true);
    },
    [sidebarFolders, spaces],
  );

  const duplicateFlow = useCallback(
    async (flow: FlowModalTarget, folderIdHint: string) => {
      const { folderId, spaceId } = resolveFolderAndSpace(
        flow,
        folderIdHint,
        sidebarFolders,
        spaces,
      );
      const type = flowTypeForModal(flow);
      await onCreateFlowOrUpdate(
        flow.name,
        true,
        flow.id,
        folderId,
        true,
        type,
        spaceId,
      );
    },
    [onCreateFlowOrUpdate, sidebarFolders, spaces],
  );

  const onCreateFlowFromModal = useCallback(
    (
      name: string,
      isEditing?: boolean,
      id?: string,
      folderId?: string,
      isCopy?: boolean,
      type?: string,
    ) => {
      void onCreateFlowOrUpdate(
        name,
        isEditing,
        id,
        folderId,
        isCopy,
        type,
        modalSpaceIdRef.current,
      ).catch(() => {});
    },
    [onCreateFlowOrUpdate],
  );

  const value = useMemo(
    () => ({
      openRenameFlow,
      openShareFlow,
      duplicateFlow,
    }),
    [openRenameFlow, openShareFlow, duplicateFlow],
  );

  const handleModalHide = useCallback(() => {
    setOpenAddFlowModal(false);
    resetModalData();
  }, [resetModalData]);

  return (
    <FlowModalContext.Provider value={value}>
      {children}
      <CreateFlowModal
        theme={authUser?.theme}
        show={openAddFlowModal}
        setIsFlowModalOpen={setIsFlowModalOpen}
        onCreateFlow={onCreateFlowFromModal}
        data={createOrEditFlow}
        onShareFlow={onShareFlow}
        onHide={handleModalHide}
      />
    </FlowModalContext.Provider>
  );
};
